<script lang="ts">
  import type { DatasetSearchResultsModel } from "./models/dataset.interface";
  import Pageination from "./components/Pageination.svelte";
  import SideSearch from "./components/SideSearch.svelte";
  import LocationSearchInput from "./components/search/LocationSearchInput.svelte";
  import WhenSearchInput from "./components/search/WhenSearchInput.svelte";
  import ObservationTypeSearchInput from "./components/search/ObservationTypeSearchInput.svelte";
  import KeywordSearchInput from "./components/search/KeywordSearchInput.svelte";
  import DatasetSearchInput from "./components/search/DatasetSearchInput.svelte";
  import type { SearchOptions } from "./models/support-service.interface";
  import MapComponent from "./components/MapComponent.svelte";
  import { SearchState } from "./services/search-service";
  import ErrorComponent from "./components/ErrorComponent.svelte";
  import TooltipHybridButton from "./components/search/TooltipHybridButton.svelte";
  import AccessibilitySearchInput from "./components/search/AccessibilitySearchInput.svelte";
  import AdvancedOptionsAccordian from "./components/search/AdvancedOptionsAccordian.svelte";
  import type {KeywordsAggregation} from "./models/common.interface";

  const PAGE_SIZE = 24;
  const searchEndpoint = new URL(process.env.SEARCH_URL);
  const searchOptions: SearchOptions = JSON.parse(
    document.getElementById("searchOptions").innerHTML
  );

  let datasetsLoaded: Promise<DatasetSearchResultsModel>;

  // variables from the side search
  let dataTypes: string;
  let latLon: string;
  let bbox = $SearchState.params["bbox"] ?? "";
  let placeId = $SearchState.params["place"] ?? "";
  let observationType = [];

  // keywordsList is irrelevant now, keywords come directly from elasticsearch datasets query
  // const keywordsList = JSON.parse(document.getElementById("keywords").innerHTML);
  let keywords: KeywordsAggregation[];

  // $: url.subscribe((url) => {
  //   console.log('Url changed: ' + url);
  //   const pageNum: number = Number(url.searchParams.get("p")) || 1;
  //   const params = $SearchState.params;
  //   params.set("limit", PAGE_SIZE.toString());
  //   params.set("offset", ((pageNum - 1) * PAGE_SIZE).toString());
  //
  //   datasetsLoaded = searchDatasets(params);
  // });

  SearchState.subscribe((search) => {
    search.params.set("limit", PAGE_SIZE.toString());
    search.params.set("offset", ((search.pageNum - 1) * PAGE_SIZE).toString());

    datasetsLoaded = searchDatasets(search.params);
  });

  async function searchDatasets(params: URLSearchParams): Promise<DatasetSearchResultsModel> {
    try {
      const response = await fetch(`${searchEndpoint}/v2/datasets?${params}`);

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const resp = await response.json();
          const message = resp.errors?.[0]?.message || resp.errorMessage || "Unknown error";
          const code = resp.errorCode || response.status;
          errorMessage = `HTTP error! status: ${code} message: ${message}`;
        } catch {
          errorMessage = `HTTP error! status: ${response.status} text: ${await response.text().catch(() => 'Unknown')}`;
        }

        let e = new Error(errorMessage);
        e.name = "HTTP_ERROR";
        throw e;
      }

      const resp: DatasetSearchResultsModel = await response.json();

      if (resp.count > 0) {
        keywords = resp.aggs?.keywords || [];
        return resp;
      }
      else {
        keywords = [];
        console.error("No datasets found!");
        let e = new Error("No search results were found based on your criteria");
        e.name = "NO_SEARCH_RESULTS";
        throw e;
      }

    } catch (err) {
      console.error("Error searching datasets:", err);
      throw err;
    }
  }
</script>

<div class="container-fluid">
  <div class="row">
    <div class="col-lg-3 gx-0">
      <!-- side search -->
      <SideSearch>
        <DatasetSearchInput slot="name" autocompleteUrl={new URL(`${searchEndpoint}/v1/autocomplete`)}/>
        <LocationSearchInput slot="where" />
        <WhenSearchInput slot="when" enabled={true} format={"Date"} />
        <!-- TODO: add this back in when the logic is fixed -->
        <AccessibilitySearchInput slot="accessLevel" />
        <AdvancedOptionsAccordian slot="advancedOptions">
            <KeywordSearchInput slot="keywords" keywords={keywords} />
            <ObservationTypeSearchInput slot="observationTypes"
                  observationTypeData={searchOptions.fields.find((f) => f.id === "observationTypes")
                      ?.values}
            />
            <!--        <FormatSearchInput-->
            <!--          slot="formats"-->
            <!--          formatData={searchOptions.fields.find((f) => f.id === "formats")?.values}-->
            <!--        />-->
        </AdvancedOptionsAccordian>
      </SideSearch>
    </div>

    <main class="col-md-9">
      <section id="dataset-results">
        <!-- Dataset Search bar -->
        <div class="row collection">
          <div class="p-5 mb-4">
            <div class="d-flex flex-column align-items-center mt-3">
              <div class="header-container">
                <h1>Dataset Search</h1>
              </div>
              <div class="instruction-container">
                <p class="lead">Search some of our most popular datasets by name or by any of the search options.</p>
              </div>
            </div>
          </div>
        </div>

<!--        {#await datasetsLoaded then dataset}-->
<!--          <div class="row">-->
<!--            <MapComponent />-->
<!--          </div>-->
<!--        {:catch error}-->
<!--          <div class="row">-->
<!--            <div class="col" style="display: none"></div>-->
<!--          </div>-->
<!--        {/await}-->

        {#await datasetsLoaded}
          <div class="center-container">
            <div
              class="spinner-border text-secondary"
              role="status"
              style="width: 3rem; height: 3rem;"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        {:then datasets}
          <div class="row">
            <div class="d-flex justify-content-center mt-2">
              <div
                class="badge"
                style="--bs-btn-border-radius: 200px; width: 20em; --bs-btn-font-weight: bold; background-color: #007EC6"
              >
                {datasets?.count || 0} collections found
              </div>
            </div>
          </div>
          <div class="row mt-2 mb-2">
            <!--TODO fix the page size to update to how many are actually on the screen-->
            <div class="col">
              Showing <b>{datasets?.results?.length || 0}</b> of {datasets?.count || 0} available collections
            </div>
          </div>

          <!-- Dataset Cards -->
          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-4">
            <div class="col-lg-6 col-md col-sm mb-3">
              <div class="card">
                <div class="card-img-top">
                  <MapComponent dataResults={undefined} homrPrefix={undefined}/>
                </div>
              </div>
            </div>
            {#each datasets?.results || [] as r}
              <div class="col mb-3">
                <!-- card body -->
                <div class="card h-100">
                  <div class="card-body">
                    <a
                      href="../datasets/{r.id}"
                      class="link"
                      style="color: #007ec6;
                                             text-decoration: none;"
                    >
                      <h5 class="card-title">{r.name}</h5>
                    </a>
                    {#each r.observationTypes as ot}
                      <h6 class="card-subtitle mb-2 text-muted">{ot.name}</h6>
                    {/each}
                    <h6 class="card-subtitle mb-2 text-muted">
                      <time datetime="2020-09-30T12:00:00+01:00">
                        {r.startDate}
                        to {r.endDate}</time
                      >
                    </h6>

                    <!-- text / dataset description -->
                    <div class="text-truncate-container">
                      <p class="card-text pt-1">{r.description}</p>
                    </div>
                  </div>

                  <!-- card footer & Tooltip-->
                  <div class="card-footer bg-transparent">
                    <div class="row align-items-center">
                      <div class="col text-muted">Topics / Keyword Tags</div>


                      <div class="col-md-auto">
                        {#if r.keywords.length > 0}
                          <!-- the wall of code inside the back ticks is hard to read, TODO: make this a reactive variable or something -->
                          <TooltipHybridButton
                            searchUrlQueryParam={r.id}
                            tooltip={`<ul class="list-unstyled m-auto" aria-label="Topics">${r.keywords.slice(0, 10).map(kw => `<li class="text-start">${kw}</li>`).join('')}</ul>`}
                          />
                        {:else}
                          <TooltipHybridButton
                            searchUrlQueryParam={r.id}
                            tooltip="<i>No keyword tags found in this dataset</i>"
                          />
                        {/if}
                        <!--                        <Tooltip target={`tooltip-${r.id}`} placement="right">-->
                        <!--                          {#if r.keywords.length > 0}-->
                        <!--                            <ul class="list-unstyled m-auto" aria-label="Topics">-->
                        <!--                              {#each r.keywords.slice(0, 10) as kw}-->
                        <!--                                <li class="text-start">{kw.name}</li>-->
                        <!--                              {/each}-->
                        <!--                            </ul>-->
                        <!--                          {:else}-->
                        <!--                            <i>No keyword tags found in this dataset</i>-->
                        <!--                          {/if}-->
                        <!--                        </Tooltip>-->
                        <!--                        <i id={`tooltip-${r.id}`} class="bi bi-info-circle"></i>-->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            {/each}
          </div>

          <!-- Pagination -->
          <div class="row">
            <div class="col">
              <Pageination pageSize={PAGE_SIZE} count={datasets?.count} />
            </div>
          </div>
        {:catch error}
          <ErrorComponent {error} />
        {/await}
      </section>
    </main>
  </div>
</div>

<style>
    /*.header-container {*/
    /*  background-color: rgba(0, 0, 0, 0.7);*/
    /*  padding: 1rem 2rem;*/
    /*  border-radius: 0.5rem;*/
    /*  margin-bottom: 1rem;*/
    /*}*/

    .header-container h1 {
        color: white;
        margin: 0;
        text-shadow: 2px 2px 7px rgba(0, 0, 0, 0.8);
    }

    /*.instruction-container {*/
    /*  background-color: rgba(0, 0, 0, 0.6);*/
    /*  padding: 0.75rem 1.5rem;*/
    /*  border-radius: 0.25rem;*/
    /*}*/

    .instruction-container p {
        color: white;
        margin: 0;
        text-shadow: 1px 1px 7px rgba(0, 0, 0, 0.8);
    }

    @media (prefers-contrast: more) {
        .header-container,
        .instruction-container {
            background-color: black;
            border: 2px solid white;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .header-container,
        .instruction-container {
            background-color: #333;
        }
    }

    .text-truncate-container {
        height: auto;
    }

    .text-truncate-container p {
        -webkit-line-clamp: 6;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .card .card-body .card-title .text-truncate-container {
        box-sizing: border-box;
        font-family: sans-serif;
    }

    .card-title {
        font-family: Arial, sans-serif;
    }

    .card {
        transition: all 0.25s ease;
    }

    .card:hover {
        box-shadow: 0 0 1.5rem rgba(0, 0, 0, 0.3);
    }

    div > i {
        background-color: transparent;
        font-size: 1.3rem;
    }

    .center-container {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        height: 100vh;
        padding-top: 15vh;
    }

    /*img {*/
    /*    object-fit: cover;*/
    /*    opacity: 0.4;*/

    /*}*/
</style>